// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  KeyCodes,
  getRTLSafeKeyCode,
  getTheme,
  mergeStyleSets,
} from "@fluentui/react";
import type {
  IContextualMenuItem,
  IContextualMenuProps,
} from "@fluentui/react/lib/ContextualMenu";
import { ContextualMenuItemType } from "@fluentui/react/lib/ContextualMenu";
import { FocusZoneDirection } from "@fluentui/react/lib/FocusZone";
import { getWindowBoundsFunction } from "@1js/acui-common";
import { appFlexMenuRender, appFlexMenuRenderV9 } from "../AppFlexMenu";
import {
  MenuSectionStyleType,
  flexMenuCalloutContentStyles,
  flexMenuStyles,
  getMenuCalloutStyles,
  getMenuClassNames,
  getMenuFooterStyles,
  getMenuSectionClassNames,
  getMenuTitleClassNames,
} from "./Menu.classNames";
import type { MenuSection } from "./MenuDefinition";
import type { MenuDefinitionToFabricMenuParameters } from "./MenuDefinitionToFabricMenuParameters";
import * as React from "react";
import { MenuHeaderWithActionV8 } from "./MenuHeaderWithAction";
import { MenuHeaderWithActionV9 } from "../AppContextualMenu/next/AppMenuItem/MenuHeaderWithAction";

function convertMenuSectionToFabricMenuItems(
  menuSection: MenuSection,
  index: number | "header" | "footer",
  menuParameters: MenuDefinitionToFabricMenuParameters,
  renderDivider?: boolean
): IContextualMenuItem {
  const {
    controlInMenuRendererFunction,
    location,
    onSubMenuPositioned,
    subComponentStyles,
    arePerfImprovementsEnabled,
    onMenuPositioned,
    onMenuItemExecuted,
    terminalParentControlId,
    useFullDescriptionLabelOnly,
    customMenuItemStyles,
    menuDefinition,
    isExperimentalFeatureEnabled,
  } = menuParameters;

  const isHeaderItem = index === "header";
  const isFooterItem = index === "footer";
  const isCommonItem = !isHeaderItem && !isFooterItem;

  let items = controlInMenuRendererFunction({
    controlProps: menuSection.controls,
    location,
    onSubMenuPositioned,
    subComponentStyles,
    arePerfImprovementsEnabled,
    onMenuPositioned,
    onMenuItemExecuted,
    terminalParentControlId,
    ...(isCommonItem && {
      useFullDescriptionLabelOnly,
      customMenuItemStyles,
    }),
    ...(isFooterItem && {
      footerItemStyles: getMenuFooterStyles,
    }),
    isExperimentalFeatureEnabled,
  });

  if (Array.isArray(items)) {
    items = items.filter((item) => !!item);
  }

  return {
    key: `section-${index}`,
    itemType: ContextualMenuItemType.Section,
    getItemClassNames: getMenuSectionClassNames(
      subComponentStyles,
      isHeaderItem
        ? MenuSectionStyleType.Header
        : isFooterItem
          ? MenuSectionStyleType.Footer
          : menuDefinition?.isFlexLayout
            ? MenuSectionStyleType.Flex
            : undefined
    ),
    itemProps: {
      lang: menuSection.lang,
    },
    sectionProps: {
      // Only create a menu section title item if it is not empty.
      title:
        !isSectionEmpty(menuSection) && menuSection.title
          ? {
              text: menuSection.title,
              key: `section-${index}-title`,
              itemType: ContextualMenuItemType.Header,
              itemProps: {
                lang: menuSection.lang,
              },
              ...(menuSection.headerAction
                ? {
                    data: {
                      action: menuSection.headerAction,
                    },
                    onRender: MenuHeaderWithActionV9,
                    contextualMenuItemAs: MenuHeaderWithActionV8,
                  }
                : {}),
            }
          : undefined,
      ...(isCommonItem && {
        bottomDivider: renderDivider,
      }),
      ...(isFooterItem && {
        topDivider: true,
        bottomDivider: false,
      }),
      items,
    },
  };
}

function isSectionEmpty(menuSection: MenuSection): boolean {
  return menuSection.controls.length === 0;
}

export function convertMenuDefinitionToFabricMenuItems(
  menuParameters: MenuDefinitionToFabricMenuParameters,
  shouldHideDivider?: (pos: number, maxPos: number) => boolean
): IContextualMenuItem[] {
  let items: IContextualMenuItem[] = [];
  const { menuDefinition, isExperimentalFeatureEnabled } = menuParameters;

  if (!menuDefinition) {
    return [];
  }

  if (menuDefinition.title) {
    items = items.concat({
      name: menuDefinition.title,
      key: "menu-title",
      itemType: ContextualMenuItemType.Header,
      getItemClassNames: getMenuTitleClassNames,
    });
  }

  menuDefinition.sections.forEach((menuSection: MenuSection, index: number) => {
    // If this isn't the last section, the next section is not empty (has untrimmed controls), render a divider.
    let renderDivider =
      index < menuDefinition.sections.length - 1 &&
      !isSectionEmpty(menuDefinition.sections[index + 1]) &&
      !(
        shouldHideDivider &&
        shouldHideDivider(index, menuDefinition.sections.length - 1)
      );

    // If this is flex layout and the menuSection is not the My addin section, not render a divider
    // We will find a formal way to remove the line: Task 8456864: [Flyout UX Improvement] Remove section line for Flyout
    if (
      isExperimentalFeatureEnabled &&
      isExperimentalFeatureEnabled(
        "Microsoft.Office.SharedOnline.AddinFlyoutLineDividerEnabled"
      ) &&
      menuDefinition.isFlexLayout &&
      menuSection &&
      menuSection.controls
    ) {
      const isLastSection = index === menuDefinition.sections.length - 1;
      const isMyAddinSection = menuSection.controls.some((section) =>
        section.id?.startsWith("MRUAddins")
      );
      renderDivider = isMyAddinSection && !isLastSection;
    }

    items = items.concat(
      convertMenuSectionToFabricMenuItems(
        menuSection,
        index,
        menuParameters,
        renderDivider
      )
    );
  });

  if (menuDefinition.footer && !menuDefinition.isFlexLayout) {
    items.push(
      convertMenuSectionToFabricMenuItems(
        menuDefinition.footer,
        "footer",
        menuParameters
      )
    );
  }

  return items.filter((item) => !!item);
}

export function convertMenuDefinitionToFabricMenuProps(
  menuParameters: MenuDefinitionToFabricMenuParameters,
  directionalHintFixed: boolean = true,
  targetWindow?: Window,
  responsiveClassName?: string,
  isV9FlexMenu?: boolean
): IContextualMenuProps {
  const {
    menuDefinition,
    customMenuClassNames,
    onMenuPositioned,
    isExperimentalFeatureEnabled,
  } = menuParameters;
  const props = (menuDefinition && menuDefinition.focusZoneProps) || {
    direction: FocusZoneDirection.bidirectional,
    checkForNoWrap: true,
    "data-no-horizontal-wrap": "true",
  };
  const theme = menuParameters.theme || getTheme();
  const menuProps: IContextualMenuProps = {
    items: convertMenuDefinitionToFabricMenuItems(menuParameters, undefined),
    isBeakVisible: false,
    directionalHintFixed,
    focusZoneProps: { ...props },
    getMenuClassNames: getMenuClassNames(
      menuDefinition?.footer && menuDefinition.isFlexLayout
        ? mergeStyleSets(flexMenuStyles, customMenuClassNames)
        : customMenuClassNames,
      responsiveClassName
    ),
    ariaLabel: menuDefinition ? menuDefinition.ariaLabel : "",
    calloutProps: {
      minPagePadding: 0,
      styles: getMenuCalloutStyles(
        theme,
        menuDefinition?.footer && menuDefinition.isFlexLayout
          ? mergeStyleSets(
              flexMenuCalloutContentStyles,
              customMenuClassNames?.subComponentStyles?.callout
            )
          : customMenuClassNames?.subComponentStyles?.callout
      ),
      onPositioned: (): void =>
        onMenuPositioned && onMenuPositioned(menuDefinition),
    },
    bounds: getWindowBoundsFunction(targetWindow),
  };
  if (menuDefinition?.footer && menuDefinition.isFlexLayout) {
    const containerRef = React.createRef<HTMLDivElement>();
    menuProps.onRenderMenuList = (menuListProps, defaultRender) =>
      isV9FlexMenu && menuDefinition.footer && defaultRender
        ? appFlexMenuRenderV9({
            defaultRender,
            headerProps: menuDefinition?.header
              ? convertMenuSectionToFabricMenuItems(
                  menuDefinition.header,
                  "header",
                  menuParameters
                )
              : undefined,
            footerProps: convertMenuSectionToFabricMenuItems(
              menuDefinition.footer,
              "footer",
              menuParameters
            ),
            expandMaxHeight: !!isExperimentalFeatureEnabled?.(
              "Microsoft.Office.SharedOnline.AddinFlyoutMaxHeightEnabled"
            ),
          })
        : !isV9FlexMenu &&
            menuDefinition.footer &&
            menuListProps &&
            defaultRender
          ? appFlexMenuRender({
              theme,
              menuListProps,
              defaultRender,
              containerRef,
              headerProps: menuDefinition?.header
                ? convertMenuSectionToFabricMenuItems(
                    menuDefinition.header,
                    "header",
                    menuParameters
                  )
                : undefined,
              footerProps: convertMenuSectionToFabricMenuItems(
                menuDefinition.footer,
                "footer",
                menuParameters
              ),
              expandMaxHeight: !!isExperimentalFeatureEnabled?.(
                "Microsoft.Office.SharedOnline.AddinFlyoutMaxHeightEnabled"
              ),
            })
          : defaultRender?.(menuListProps) || null;
    if (menuProps.focusZoneProps) {
      menuProps.focusZoneProps.shouldEnterInnerZone = (ev) =>
        ev.which === getRTLSafeKeyCode(KeyCodes.right);
    }
    if (menuProps.calloutProps) {
      const onPositioned = menuProps.calloutProps.onPositioned;
      menuProps.calloutProps.onPositioned = (e) => {
        const el = containerRef.current;
        if (el) {
          el.scrollTop = 0;
        }
        return onPositioned?.(e);
      };
      menuProps.calloutProps.calloutWidth = 360;
      menuProps.calloutProps.calloutMaxWidth = 360;
    }
  }
  return menuProps;
}

export function convertMenuDefinitionToFabricSubMenuProps(
  menuParameters: MenuDefinitionToFabricMenuParameters,
  subMenuId?: string
): IContextualMenuProps {
  const { menuDefinition, onSubMenuPositioned } = menuParameters;

  const theme = menuParameters.theme || getTheme();

  return {
    ...convertMenuDefinitionToFabricMenuProps(
      menuParameters,
      undefined /**directionHintFixed */
    ),
    directionalHintFixed: undefined,
    calloutProps: {
      styles: getMenuCalloutStyles(
        theme,
        menuDefinition?.footer && menuDefinition.isFlexLayout
          ? flexMenuCalloutContentStyles
          : undefined
      ),
      onPositioned: (): void =>
        onSubMenuPositioned && onSubMenuPositioned(menuDefinition, subMenuId),
    },
    id: subMenuId + "MenuId",
  };
}
